import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './TaskTimer.css';

function TaskTimer({ task, listId, allTasks = [], onTimerUpdate }) {
  const { t } = useLanguage();
  const [currentTime, setCurrentTime] = useState(0);
  const [isActive, setIsActive] = useState(task.timer_status === 'active');
  const [error, setError] = useState('');
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  // Verificar si hay otra tarea activa
  const hasOtherActiveTask = allTasks.some(
    t => t.id !== task.id && t.timer_status === 'active'
  );

  // Calcular tiempo inicial
  useEffect(() => {
    const baseTime = task.total_time_seconds || 0;
    setCurrentTime(baseTime);

    if (task.timer_status === 'active' && task.last_active_at) {
      const startTime = new Date(task.last_active_at).getTime();
      startTimeRef.current = startTime;
      setIsActive(true);
    } else {
      setIsActive(false);
      startTimeRef.current = null;
    }
  }, [task.timer_status, task.last_active_at, task.total_time_seconds]);

  // Actualizar timer cada segundo si está activo
  useEffect(() => {
    if (isActive && startTimeRef.current) {
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTimeRef.current) / 1000);
        const baseTime = task.total_time_seconds || 0;
        setCurrentTime(baseTime + elapsed);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, task.total_time_seconds]);

  const formatTime = (seconds) => {
    if (seconds === 0) return t('timerZeroTime');
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return t('timerTimeFormat')
        .replace('{hours}', hours)
        .replace('{minutes}', minutes)
        .replace('{seconds}', secs);
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const handleTimerAction = async (action) => {
    setError(''); // Limpiar error previo
    
    // Si se intenta iniciar y ya hay otra tarea activa, rechazar antes de hacer la petición
    if (action === 'start' && hasOtherActiveTask) {
      setError(t('timerErrorActiveTask'));
      return;
    }

    try {
      const method = action === 'start' ? 'POST' : 'PATCH';
      const response = await fetch(`/api/lists/${listId}/tasks/${task.id}/timer`, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Actualizar con los datos del backend
        const updatedTask = data.task;
        
        if (action === 'start' || action === 'resume') {
          setIsActive(true);
          if (updatedTask.last_active_at) {
            startTimeRef.current = new Date(updatedTask.last_active_at).getTime();
          } else {
            startTimeRef.current = Date.now();
          }
          setCurrentTime(updatedTask.total_time_seconds || 0);
        } else if (action === 'pause') {
          setIsActive(false);
          setCurrentTime(updatedTask.total_time_seconds || currentTime);
          startTimeRef.current = null;
        } else if (action === 'finish') {
          setIsActive(false);
          setCurrentTime(updatedTask.total_time_seconds || 0);
          startTimeRef.current = null;
        }

        // Notificar al componente padre para actualizar la lista
        if (onTimerUpdate) {
          onTimerUpdate(updatedTask);
        }
      } else {
        // Manejar error del backend
        setError(data.error || t('timerErrorGeneric'));
      }
    } catch (err) {
      console.error('Error en acción del timer:', err);
      setError(t('timerErrorGeneric'));
    }
  };

  const getTimerStatus = () => {
    if (task.timer_status === 'active') return 'active';
    if (task.timer_status === 'paused') return 'paused';
    if (task.timer_status === 'completed') return 'completed';
    return 'not_started';
  };

  const status = getTimerStatus();

  return (
    <div className={`task-timer ${status}`}>
      <div className="timer-display">
        <span className="timer-icon">⏱️</span>
        <span className="timer-time">{formatTime(currentTime)}</span>
      </div>

      {error && (
        <div className="timer-error">
          {error}
        </div>
      )}
      
      <div className="timer-controls">
        {status === 'not_started' && (
          <button
            className="timer-button timer-button-start"
            disabled={hasOtherActiveTask}
            onClick={(e) => {
              e.stopPropagation();
              handleTimerAction('start');
            }}
            title={hasOtherActiveTask ? t('timerErrorActiveTask') : ''}
          >
            {t('timerStart')}
          </button>
        )}

        {status === 'active' && (
          <>
            <button
              className="timer-button timer-button-pause"
              onClick={(e) => {
                e.stopPropagation();
                handleTimerAction('pause');
              }}
            >
              {t('timerPause')}
            </button>
            <button
              className="timer-button timer-button-finish"
              onClick={(e) => {
                e.stopPropagation();
                handleTimerAction('finish');
              }}
            >
              {t('timerFinish')}
            </button>
          </>
        )}

        {status === 'paused' && (
          <>
            <button
              className="timer-button timer-button-resume"
              onClick={(e) => {
                e.stopPropagation();
                handleTimerAction('resume');
              }}
            >
              {t('timerResume')}
            </button>
            <button
              className="timer-button timer-button-finish"
              onClick={(e) => {
                e.stopPropagation();
                handleTimerAction('finish');
              }}
            >
              {t('timerFinish')}
            </button>
          </>
        )}

        {status === 'completed' && (
          <span className="timer-status-text">{t('timerCompleted')}</span>
        )}
      </div>
    </div>
  );
}

export default TaskTimer;

